// app/javascript/lib/themes.ts
// Client-side theme registry — single source of truth for all theme definitions.

export type ThemeMode = 'light' | 'dark'

export interface ThemeFamily {
  id: string
  displayName: string
  swatches: [string, string, string]
  lightThemeName: string
  darkThemeName: string
}

export interface ThemeDefinition {
  name: string
  displayName: string
  mode: ThemeMode
  family: string
  swatches: [string, string, string]
  vars: Record<string, string>
}

// ---------------------------------------------------------------------------
// 5 Unique Theme Families
// Each family has a light variant and a dark variant
// ---------------------------------------------------------------------------

export const THEME_FAMILIES: ThemeFamily[] = [
  {
    id: 'glass',
    displayName: 'Glass',
    swatches: ['hsl(245 70% 58%)', 'hsl(280 65% 62%)', 'hsl(220 25% 96%)'],
    lightThemeName: 'glass-light',
    darkThemeName: 'glass-dark',
  },
  {
    id: 'solarized',
    displayName: 'Solarized',
    swatches: ['hsl(196 77% 40%)', 'hsl(68 65% 40%)', 'hsl(44 87% 94%)'],
    lightThemeName: 'solarized-light',
    darkThemeName: 'solarized-dark',
  },
  {
    id: 'pro',
    displayName: 'Dark Pro / Pro',
    swatches: ['hsl(250 80% 65%)', 'hsl(280 75% 68%)', 'hsl(222 26% 8%)'],
    lightThemeName: 'pro-light',
    darkThemeName: 'dark-pro',
  },
  {
    id: 'atom',
    displayName: 'Atom One',
    swatches: ['hsl(28 90% 60%)', 'hsl(355 80% 62%)', 'hsl(220 13% 14%)'],
    lightThemeName: 'atom-light',
    darkThemeName: 'atom-dark',
  },
  {
    id: 'aurora',
    displayName: 'Aurora',
    swatches: ['hsl(160 85% 52%)', 'hsl(315 90% 65%)', 'hsl(215 35% 7%)'],
    lightThemeName: 'aurora-light',
    darkThemeName: 'aurora',
  },
]

// ---------------------------------------------------------------------------
// Theme Definitions
// ---------------------------------------------------------------------------

const GLASS_LIGHT: ThemeDefinition = { name: 'glass-light', displayName: 'Glass', mode: 'light', family: 'glass', swatches: ['hsl(245 70% 58%)', 'hsl(280 65% 62%)', 'hsl(220 25% 96%)'], vars: {} }
const GLASS_DARK: ThemeDefinition = { name: 'glass-dark', displayName: 'Glass', mode: 'dark', family: 'glass', swatches: ['hsl(245 75% 65%)', 'hsl(280 70% 68%)', 'hsl(224 28% 10%)'], vars: {} }

const SOLARIZED_LIGHT: ThemeDefinition = { name: 'solarized-light', displayName: 'Solarized', mode: 'light', family: 'solarized', swatches: ['hsl(196 77% 40%)', 'hsl(68 65% 40%)', 'hsl(44 87% 94%)'], vars: {} }
const SOLARIZED_DARK: ThemeDefinition = { name: 'solarized-dark', displayName: 'Solarized', mode: 'dark', family: 'solarized', swatches: ['hsl(196 80% 55%)', 'hsl(68 70% 50%)', 'hsl(192 80% 9%)'], vars: {} }

const PRO_LIGHT: ThemeDefinition = { name: 'pro-light', displayName: 'Dark Pro', mode: 'light', family: 'pro', swatches: ['hsl(250 75% 55%)', 'hsl(280 70% 58%)', 'hsl(220 20% 97%)'], vars: {} }
const DARK_PRO: ThemeDefinition = { name: 'dark-pro', displayName: 'Dark Pro', mode: 'dark', family: 'pro', swatches: ['hsl(250 80% 65%)', 'hsl(280 75% 68%)', 'hsl(222 26% 8%)'], vars: {} }

const ATOM_LIGHT: ThemeDefinition = { name: 'atom-light', displayName: 'Atom One', mode: 'light', family: 'atom', swatches: ['hsl(28 90% 50%)', 'hsl(355 75% 52%)', 'hsl(220 15% 96%)'], vars: {} }
const ATOM_DARK: ThemeDefinition = { name: 'atom-dark', displayName: 'Atom One', mode: 'dark', family: 'atom', swatches: ['hsl(28 90% 60%)', 'hsl(355 80% 62%)', 'hsl(220 13% 14%)'], vars: {} }

const AURORA_LIGHT: ThemeDefinition = { name: 'aurora-light', displayName: 'Aurora', mode: 'light', family: 'aurora', swatches: ['hsl(160 80% 40%)', 'hsl(315 80% 55%)', 'hsl(215 30% 96%)'], vars: {} }
const AURORA: ThemeDefinition = { name: 'aurora', displayName: 'Aurora', mode: 'dark', family: 'aurora', swatches: ['hsl(160 85% 52%)', 'hsl(315 90% 65%)', 'hsl(215 35% 7%)'], vars: {} }

export const BUILTIN_THEMES: ThemeDefinition[] = [
  GLASS_LIGHT, GLASS_DARK,
  SOLARIZED_LIGHT, SOLARIZED_DARK,
  PRO_LIGHT, DARK_PRO,
  ATOM_LIGHT, ATOM_DARK,
  AURORA_LIGHT, AURORA,
]

export const BUILTIN_THEME_MAP: Record<string, ThemeDefinition> = Object.fromEntries(
  BUILTIN_THEMES.map(t => [t.name, t])
)

export interface DbTheme {
  name: string
  display_name: string
  mode: ThemeMode
  is_active: boolean
  sort_order: number
  css_overrides?: Record<string, string> | null
  metadata?: { swatches?: [string, string, string]; family?: string }
}

export function mergeThemes(dbThemes: DbTheme[]): ThemeDefinition[] {
  return dbThemes.map(db => {
    const builtin = BUILTIN_THEME_MAP[db.name]
    return {
      name: db.name,
      displayName: db.display_name,
      mode: db.mode,
      family: db.metadata?.family ?? builtin?.family ?? db.name.split('-')[0],
      swatches: db.metadata?.swatches ?? builtin?.swatches ?? ['#888', '#999', '#eee'],
      vars: { ...(builtin?.vars ?? {}), ...(db.css_overrides ?? {}) },
    }
  })
}

export function applyTheme(theme: ThemeDefinition): void {
  const root = document.documentElement
  root.setAttribute('data-theme', theme.name)
  root.classList.remove('light', 'dark')
  root.classList.add(theme.mode)

  for (const [prop, value] of Object.entries(theme.vars)) {
    root.style.setProperty(prop, value)
  }
}

export function resolveInitialThemeName(
  serverThemeName?: string | null,
  fallback = 'glass-light'
): string {
  if (typeof window === 'undefined') return serverThemeName ?? fallback
  return localStorage.getItem('themeName') ?? serverThemeName ?? fallback
}
